import { encryptStr, isInStudent, saveTokenToStorage } from '@yuanfudao/metis-unified-login'
import config from '@yuanfudao/metis-unified-login/es/config'
import type { HTTPResponse } from '@yuanfudao/metis-unified-login/es/types/common'

export interface PasswordLoginResult {
  accessToken: string
  refreshToken: string
  needResetPassword?: boolean
  resetPasswordTicket?: string
  resetPasswordPhone?: string
  resetPasswordExpireAt?: number
}

export interface ResetPasswordSession {
  phone: string
  ticket: string
  loginId: string
  expireAt?: number
}

function encrypt(value: string) {
  const encrypted = encryptStr(value)
  if (!encrypted) throw new Error('加密失败，请刷新页面重试')
  return encrypted
}

// 这些接口在登录前调用，不经过 SDK 的已登录检查；沿用 SDK 的环境地址规则。
async function authRequest<T>(path: string, params: Record<string, string>): Promise<T> {
  const base = process.env.REACT_APP_ENV === 'development' || window.location.host.includes('-venv.')
    ? '' : config.HTTP_BASE_URL
  const body = new URLSearchParams(params)
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 20000)
  try {
    const response = await fetch(`${base}/metis-atlas-account/api/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      signal: controller.signal,
    })
    const data: HTTPResponse<T> = await response.json()
    if (!response.ok || data.code !== '0') {
      throw Object.assign(new Error(data.message || (data.code === '40100' ? '账号或密码错误' : '操作失败，请稍后重试')), { code: data.code })
    }
    return data.result
  } finally {
    window.clearTimeout(timeout)
  }
}

export function passwordLogin(loginId: string, password: string) {
  return authRequest<PasswordLoginResult>('auth/password-login', {
    loginId: encrypt(loginId),
    password: encrypt(password),
    userAgent: 'web',
  })
}

export function resetPassword(session: ResetPasswordSession, password: string) {
  return authRequest<null>('account/password/reset/ticket', {
    phone: encrypt(session.phone),
    ticket: session.ticket,
    password: encrypt(password),
  })
}

// 保持品牌配置的加密参数和返回结构；初始密码登录不得保存临时 token。
export async function loginByPassword(params: { phone: string, password: string }) {
  const student = isInStudent()
  const result = await authRequest<PasswordLoginResult>(student ? 'student-auth/phone-password-login' : 'auth/password-login', student
    ? {
      phone: params.phone,
      password: params.password,
      userAgent: 'web',
    }
    : {
      loginId: params.phone,
      password: params.password,
      userAgent: 'web',
    })
  if (!result.needResetPassword) {
    if (!result.accessToken || !result.refreshToken) throw new Error('登录失败，请重试')
    saveTokenToStorage(result.accessToken, result.refreshToken)
  }
  return { data: { code: '0', message: '', result } }
}
